const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';

const TGP_VISIBLE_CLASSNAME = 'tgp-is-visible';
const TGP_ANIMATED_CLASSNAME = 'tgp-has-animated';

const TGP_SETTINGS = {
  revealThreshold: 0.12,
  revealRootMargin: '0px 0px -8% 0px',
  zoomStrength: 0.045,
  staggerDelay: 90,
};

/* --------------------------------------------------
   REDUCED MOTION
-------------------------------------------------- */

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/* --------------------------------------------------
   SCROLL REVEAL
-------------------------------------------------- */

function revealElement(entry, observer, index = 0) {
  const element = entry.target;

  if (!entry.isIntersecting) return;

  const customDelay = element.dataset.animationDelay;
  const cascadeDelay = element.hasAttribute('data-cascade')
    ? index * TGP_SETTINGS.staggerDelay
    : 0;

  const delay = customDelay
    ? Number(customDelay)
    : cascadeDelay;

  element.style.setProperty('--tgp-animation-delay', `${delay}ms`);
  element.style.setProperty('--animation-order', index);

  requestAnimationFrame(() => {
    element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    element.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    element.classList.add(TGP_VISIBLE_CLASSNAME);
    element.classList.add(TGP_ANIMATED_CLASSNAME);
  });

  observer.unobserve(element);
}

function initializeScrollAnimationTrigger(
  rootEl = document,
  isDesignModeEvent = false
) {
  const animationTriggerElements = Array.from(
    rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME)
  );

  if (animationTriggerElements.length === 0) return;

  if (prefersReducedMotion()) {
    animationTriggerElements.forEach((element) => {
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.classList.add(TGP_VISIBLE_CLASSNAME);
    });
    return;
  }

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.classList.add(TGP_VISIBLE_CLASSNAME);
    });
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry, index) => {
        revealElement(entry, observer, index);
      });
    },
    {
      threshold: TGP_SETTINGS.revealThreshold,
      rootMargin: TGP_SETTINGS.revealRootMargin,
    }
  );

  animationTriggerElements.forEach((element) => {
    if (!element.classList.contains(TGP_ANIMATED_CLASSNAME)) {
      element.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      observer.observe(element);
    }
  });
}

/* --------------------------------------------------
   SCROLL ZOOM
-------------------------------------------------- */

function initializeScrollZoomAnimationTrigger(rootEl = document) {
  if (prefersReducedMotion()) return;

  const animationTriggerElements = Array.from(
    rootEl.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME)
  );

  if (animationTriggerElements.length === 0) return;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    let ticking = false;

    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          elementIsVisible = entry.isIntersecting;
        });
      },
      {
        rootMargin: '15% 0px 15% 0px',
      }
    );

    visibilityObserver.observe(element);

    const updateZoom = () => {
      if (!elementIsVisible) {
        ticking = false;
        return;
      }

      const visiblePercentage = percentageSeen(element);
      const progress = Math.min(Math.max(visiblePercentage / 100, 0), 1);

      const easedProgress = easeOutCubic(progress);
      const zoomRatio = 1 + TGP_SETTINGS.zoomStrength * easedProgress;

      element.style.setProperty(
        '--zoom-in-ratio',
        zoomRatio.toFixed(4)
      );

      ticking = false;
    };

    updateZoom();

    window.addEventListener(
      'scroll',
      () => {
        if (!elementIsVisible || ticking) return;

        ticking = true;
        window.requestAnimationFrame(updateZoom);
      },
      { passive: true }
    );

    window.addEventListener('resize', () => {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(updateZoom);
    });
  });
}

/* --------------------------------------------------
   HELPERS
-------------------------------------------------- */

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const elementRect = element.getBoundingClientRect();
  const elementHeight = element.offsetHeight;

  if (elementRect.top > viewportHeight) {
    return 0;
  }

  if (elementRect.bottom < 0) {
    return 100;
  }

  const visibleDistance = viewportHeight - elementRect.top;
  const totalDistance = viewportHeight + elementHeight;

  return Math.min(
    100,
    Math.max(0, (visibleDistance / totalDistance) * 100)
  );
}

function easeOutCubic(value) {
  return 1 - Math.pow(1 - value, 3);
}

/* --------------------------------------------------
   INITIALIZATION
-------------------------------------------------- */

function initializeTGPAnimations(rootEl = document, isDesignModeEvent = false) {
  initializeScrollAnimationTrigger(rootEl, isDesignModeEvent);
  initializeScrollZoomAnimationTrigger(rootEl);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeTGPAnimations();
});

/* --------------------------------------------------
   SHOPIFY THEME EDITOR
-------------------------------------------------- */

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeTGPAnimations(event.target, true);
  });

  document.addEventListener('shopify:section:reorder', () => {
    initializeTGPAnimations(document, true);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const selectedBlock = event.target;

    selectedBlock.classList.remove(
      SCROLL_ANIMATION_OFFSCREEN_CLASSNAME
    );

    selectedBlock.classList.add(TGP_VISIBLE_CLASSNAME);
  });
}